// =============================================================================
// NameSetup — first-login gate.
//
// Rendered by RequireAuth when an authenticated user has no displayName set.
// Captures the user's real name (e.g., "Andrew Monahan") and writes it to
// Firebase Auth via updateProfile + to the users/{uid} doc. After save we
// call refreshProfile() so AuthProvider re-derives the profile shape and the
// children render with the new name everywhere.
//
// One field, two buttons (Continue + Skip — skip uses the email local-part
// title-cased so we never store empty). Editorial brand grammar (.w-shell
// + .w-eye + .w-h3 + Crimson Pro). No portal navigation needed; this is
// purely client-side once the user is authed.
// =============================================================================
import { useState } from "react";
import { updateProfile } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../lib/firebase.js";
import { useAuth } from "./AuthProvider.jsx";

export function NameSetup() {
  const { profile, refreshProfile } = useAuth();
  const guess = pickGuess(profile);
  const [value, setValue] = useState(guess);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    const trimmed = (value || "").trim();
    if (trimmed.length < 2) {
      setError("Give us at least two characters.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in. Refresh and try again.");

      // 1. Firebase Auth — this is the canonical store for user-level display
      //    name. Survives across devices and across SEVRIC apps in the same
      //    project.
      await updateProfile(user, { displayName: trimmed });

      // 2. Mirror to the users/{uid} doc so widgets that read displayName
      //    from the user doc (when we add that later) pick it up too. Self-
      //    update is allowed by rules for safe fields.
      try {
        await setDoc(
          doc(db, "users", user.uid),
          { displayName: trimmed, displayNameSetAt: serverTimestamp() },
          { merge: true },
        );
      } catch (err) {
        // Mirror failure is not fatal — Auth still has it, every author
        // snapshot from here forward will include displayName.
        console.warn("[NameSetup] users doc mirror failed:", err?.message);
      }

      // 3. Re-derive the profile shape so children render with the new name.
      await refreshProfile();
    } catch (err) {
      console.warn("[NameSetup] save failed:", err);
      setError(err?.message || "Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter") { e.preventDefault(); save(); }
  }

  return (
    <main className="setup-stage">
      <div className="setup-card w-shell">
        <div className="w-eye">
          <span className="w-eye-dot" />
          ONE STEP · WELCOME TO SEVRIC AI
        </div>
        <h1 className="setup-h1">
          What should we <em>call you</em>?
        </h1>
        <p className="setup-sub">
          We pin your name to every reading, charge, and note so the team
          always knows who did what. You can change it later in Settings.
        </p>

        <label className="setup-label" htmlFor="setup-name">Your name</label>
        <input
          id="setup-name"
          type="text"
          className="setup-input"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="e.g., Andrew Monahan"
          maxLength={64}
          disabled={saving}
        />

        {error && <div className="setup-error">{error}</div>}

        <div className="setup-actions">
          <button
            type="button"
            className="setup-btn primary"
            onClick={save}
            disabled={saving || (value || "").trim().length < 2}
          >
            {saving ? "Saving…" : "Continue →"}
          </button>
        </div>

        <div className="setup-foot">
          Signed in as <b>{profile?.email || "—"}</b>
        </div>
      </div>
    </main>
  );
}

// Best-guess starting value so the user doesn't stare at an empty field.
// Uses Firebase Auth's displayName if set somehow, then the email's local
// part with separators cleaned up ("andrew.monahan" → "Andrew Monahan").
function pickGuess(profile) {
  if (profile?.displayName) return profile.displayName;
  const local = (profile?.email || "").split("@")[0];
  if (!local) return "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  return parts.map((p) => p[0].toUpperCase() + p.slice(1)).join(" ");
}
